import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PropertyContext = createContext(null);

export function PropertyProvider({ children }) {
    const { user } = useAuth();
    const [properties, setProperties] = useState([]);
    // Single-property model: default to first property once loaded.
    // We NEVER auto-select "all" any more — the whole app is scoped to one
    // property at a time. When the user has multiple properties, they can
    // switch via the topbar; that switcher is hidden entirely for
    // single-property hotels (see Topbar.jsx).
    const [activePropertyId, setActivePropertyId] = useState(() => localStorage.getItem("akm_active_property") || "");

    const reload = useCallback(async () => {
        if (!user || user.role === "super_admin") {
            setProperties([]);
            return;
        }
        try {
            const { data } = await api.get("/properties");
            setProperties(data);
            // Legacy "all" value → migrate to the first real property id.
            const stillValid = activePropertyId && activePropertyId !== "all"
                && data.find((p) => p.id === activePropertyId);
            if (!stillValid && data.length > 0) {
                setActivePropertyId(data[0].id);
                localStorage.setItem("akm_active_property", data[0].id);
            }
        } catch {
            setProperties([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        reload();
    }, [reload]);

    const setActive = (id) => {
        setActivePropertyId(id);
        localStorage.setItem("akm_active_property", id);
    };

    return (
        <PropertyContext.Provider value={{ properties, activePropertyId, setActive, reload }}>
            {children}
        </PropertyContext.Provider>
    );
}

export function useProperties() {
    const ctx = useContext(PropertyContext);
    if (!ctx) throw new Error("useProperties must be used within PropertyProvider");
    return ctx;
}
